/* Two languages, one dictionary, no dependency.
 *
 * Norwegian first among equals: this is a Norwegian company, the sites are
 * Norwegian, and the people reading a level on a quay are Norwegian. English
 * exists because the customers will not all be.
 *
 * WHY NOT i18next
 *
 * The whole string surface is one screen of an application used by a handful of
 * operators. i18next is 40 kB, a plugin ecosystem and an initialisation dance,
 * and it would be the largest dependency in a repo whose only runtime
 * dependencies are React and hls.js. A dictionary and a lookup do the job, and
 * the type system catches a missing key at build time -- which is the one thing
 * a runtime library cannot do for us.
 *
 * VOICE
 *
 * Short, declarative, and never explanatory. The previous copy told people how
 * the machine works ("it asks the unit to start streaming, so it takes a few
 * seconds to begin"); nobody remembers that and nobody needed it. A label
 * should be the shortest true thing, and where a line earns its place it should
 * be worth repeating.
 *
 * The product is listening. Not monitoring, not sensing: `lytt` is already the
 * hostname a node talks to, the mark's rings travel inward because a hydrophone
 * only ever receives, and the ground rules are about keeping an honest record
 * of what was heard. So the words are Listen, Places, Recordings, and the
 * record -- plain words, used consistently, rather than a coined vocabulary
 * nobody can repeat back.
 */

export type Lang = 'nb' | 'en'

export const LANGS: { code: Lang; label: string }[] = [
  { code: 'nb', label: 'Norsk' },
  { code: 'en', label: 'English' },
]

/* The dictionary is keyed in English because a key that is also a readable
 * sentence survives a grep, and because a missing Norwegian string is then
 * obvious in review rather than showing as a bare key in production. */
const NB: Record<string, string> = {
  // Navigation. Nouns, not equipment.
  // A listening post is an installation with a purpose. "Lytting" is a gerund:
  // an activity with no object and no outcome, which reads as something
  // happening to you rather than something you have put in the water.
  'Listening posts': 'Lytteposter',
  'Posts': 'Poster',
  'Places': 'Steder',
  'Hydrophones': 'Hydrofoner',
  'Delivery': 'Levering',

  // The one line the product puts on a wall, and the only one in the product.
  // True of acoustics and of nothing else being sold into these sites: a
  // failing pump, a boat, a predator and a change in fish behaviour are all
  // audible before they are visible.
  'You hear it before you see it.': 'Du hører det før du ser det.',

  // Fleet
  'Add a hydrophone': 'Legg til hydrofon',
  'Add your first hydrophone': 'Legg til din første hydrofon',
  'Nothing deployed yet': 'Ingenting i sjøen ennå',
  'Listen': 'Lytt',
  // The log of what was heard. "Logg" is the word a skipper
  // already uses for a record of what happened on a watch.
  'Log': 'Logg',
  'Manage': 'Administrer',
  'Open deployment': 'Åpne utsetting',
  'Unit details': 'Om enheten',
  'Bring it in': 'Ta den opp',
  'Retired': 'Pensjonert',
  'now': 'nå',
  'Listening now': 'Lytter nå',
  'Last heard': 'Sist hørt',

  // Mission tabs
  'Overview': 'Oversikt',
  'Sound': 'Lyd',
  'Recordings': 'Opptak',
  /* The clips a person recorded themselves, in the aside beside the player.
     Distinct from `Recordings` because the archive heading on the same screen
     is now Opptak, and two identical headings a hand's width apart meaning
     different things is worse than a slightly narrower word. These really are
     clips: short, deliberate, and saved by someone. */
  'Clips': 'Klipp',
  'Deployment': 'Utsetting',

  // Places
  'Details': 'Detaljer',
  'Name': 'Navn',
  'Position': 'Posisjon',
  'Operator': 'Operatør',
  'Notes': 'Notater',
  'Edit site': 'Rediger sted',
  'Edit node': 'Rediger enhet',
  'Edit hydrophone': 'Rediger hydrofon',
  'Save': 'Lagre',
  'Cancel': 'Avbryt',
  'Saving…': 'Lagrer…',

  // Services
  'Services': 'Tjenester',
  'Record': 'Opptak',
  'Upload': 'Opplasting',
  'Live': 'Direkte',
  'running': 'kjører',
  'stopped': 'stoppet',
  'streaming': 'sender',
  'off': 'av',

  // Recordings
  'By day': 'Etter dag',
  'Recordings you can listen to': 'Opptak du kan høre på',
  'What the node noticed': 'Hva enheten merket seg',
  'Started': 'Startet',
  'Length': 'Lengde',
  'Capture gain': 'Opptaksforsterkning',
  'Saved by': 'Lagret av',
  'Size': 'Størrelse',

  // Calibration
  'Calibration': 'Kalibrering',
  'Model': 'Modell',
  'Serial': 'Serienummer',
  'not calibrated': 'ikke kalibrert',

  // States
  'Nothing here yet': 'Ingenting her ennå',
  'Never deployed': 'Aldri satt ut',

  // The security document. The body stays in English -- it is written for
  // customer security reviews, which arrive in English even from Norwegian
  // buyers, and translating prose that has to stay precise is how two versions
  // come to say different things. The navigation is translated, because the
  // navigation always is.
  'Security': 'Sikkerhet',
  'Written to be shared. Print this page to send it.':
    'Skrevet for å deles. Skriv ut denne siden for å sende den.',

  // Getting in, and getting back in. The one part of the product a person
  // meets before they have any reason to trust it, which is why none of these
  // strings hedge: "hvis den adressen har en konto" is deliberate, and
  // deliberately not "vi fant ingen konto".
  'Forgotten your password?': 'Glemt passordet?',
  'Accounts are created by invitation. There is no sign-up.':
    'Kontoer opprettes ved invitasjon. Det finnes ingen registrering.',
  'Reset your password': 'Nullstill passordet',
  'Sign in by email': 'Logg inn med e-post',
  'Send me a link': 'Send meg en lenke',
  'Send me a sign-in link instead': 'Send meg en innloggingslenke i stedet',
  'I would rather reset my password': 'Jeg vil heller nullstille passordet',
  'Sending…': 'Sender…',
  'If that address has an account, a link is on its way. It is good for one use.':
    'Har den adressen en konto, er en lenke på vei. Den kan brukes én gang.',
  'Back to sign in': 'Tilbake til innlogging',
  'Ask for a new one': 'Be om en ny',
  'Checking the link…': 'Sjekker lenken…',
  'This link has expired or has already been used.':
    'Denne lenken er utløpt eller allerede brukt.',
  'Choose a password': 'Velg et passord',
  'Set a new password': 'Sett nytt passord',
  'At least 12 characters. Length beats punctuation.':
    'Minst 12 tegn. Lengde slår tegnsetting.',
  'Repeat it': 'Gjenta det',
  'The two do not match.': 'De to er ikke like.',
  // 'Saving…' is already above, from the mission editors.
  'Save and sign in': 'Lagre og logg inn',
  'Every other signed-in device will be signed out.':
    'Alle andre påloggede enheter blir logget ut.',
  'Signing you in…': 'Logger deg inn…',
  'Confirming…': 'Bekrefter…',
  'Confirmed. That is the address you sign in with now.':
    'Bekreftet. Det er adressen du logger inn med nå.',

  // The account page.
  'Your account': 'Kontoen din',
  'Password': 'Passord',
  'Change password': 'Endre passord',
  'Changed. Every other device has been signed out.':
    'Endret. Alle andre enheter er logget ut.',
  'Email address': 'E-postadresse',
  'Change address': 'Endre adresse',
  'Nothing changes until you confirm it from the new address.':
    'Ingenting endres før du bekrefter fra den nye adressen.',
  'If that address is free, a confirmation link is on its way to it. The old address has been told.':
    'Er den adressen ledig, er en bekreftelseslenke på vei dit. Den gamle adressen er varslet.',
  'Where you are signed in': 'Hvor du er logget inn',
  'Last seen': 'Sist sett',
  'Signed in': 'Logget inn',
  'Address': 'Adresse',
  'Browser': 'Nettleser',
  'Nothing to show': 'Ingenting å vise',
  'This is the only signed-in session.': 'Dette er den eneste påloggede økten.',
  'Sign out everywhere else': 'Logg ut alle andre steder',

  // Onboarding a customer.
  'Operators': 'Kunder',
  'The companies that own the farms, and the people who work there.':
    'Selskapene som eier lokalitetene, og folkene som jobber der.',
  'Add a company': 'Legg til selskap',
  'New company': 'Nytt selskap',
  'Search the aquaculture register': 'Søk i Akvakulturregisteret',
  'Type a company name or organisasjonsnummer.':
    'Skriv et selskapsnavn eller et organisasjonsnummer.',
  'Not in the register — enter a name instead':
    'Ikke i registeret — skriv inn et navn i stedet',
  'Search the register instead': 'Søk i registeret i stedet',
  'Choose a different company': 'Velg et annet selskap',
  'Linked to the register, so its farms, vessels and lice data can be matched.':
    'Koblet til registeret, så lokaliteter, fartøy og lusedata kan kobles sammen.',
  'Org. no.': 'Org.nr.',
  'Company': 'Selskap',
  'Farms': 'Lokaliteter',
  'farms': 'lokaliteter',
  'Areas': 'Områder',
  'People': 'Personer',
  'No companies yet': 'Ingen selskaper ennå',
  'deployed farm has no owner, so no customer can see it.':
    'utsatt lokalitet har ingen eier, så ingen kunde kan se den.',
  'deployed farms have no owner, so no customer can see them.':
    'utsatte lokaliteter har ingen eier, så ingen kunde kan se dem.',
  // The customer's own product.
  'Your farms': 'Lokalitetene dine',
  // The rail, for a customer. One entry per product they bought.
  'Which farm do you want to listen to?': 'Hvilken lokalitet vil du høre på?',
  'Which farm do you want the forecast for?': 'Hvilken lokalitet vil du se varselet for?',
  'Which farm do you want the log for?': 'Hvilken lokalitet vil du se loggen for?',
  'Which farm do you want the feeding for?': 'Hvilken lokalitet vil du se fôringen for?',
  'Which farm do you want the appetite for?': 'Hvilken lokalitet vil du se appetitten for?',
  'Appetite': 'Appetitt',
  'This farm does not have Appetite.': 'Denne lokaliteten har ikke Appetitt.',
  'Hungry': 'Sulten',
  'Meh': 'Nja',
  'Not interested': 'Ikke interessert',
  'Drop feed': 'Kast fôr',
  // The membership role, which is what a customer was actually invited as --
  // `users.role` is `viewer` on every customer account and means nothing.
  'Company manager': 'Bedriftsansvarlig',
  'Area manager': 'Områdeansvarlig',
  'Site manager': 'Lokalitetsansvarlig',
  'Vessel': 'Fartøy',
  'Subsupplier': 'Underleverandør',
  'No farms have been assigned to you. Phoniq sets that up.':
    'Ingen lokaliteter er tildelt deg ennå. Phoniq setter det opp.',
  'Hydrophone': 'Hydrofon',
  'none deployed': 'ingen utsatt',
  'listening now': 'lytter nå',
  'last heard': 'sist hørt',
  'not heard from': 'ikke hørt fra',
  'Lice': 'Lus',

  /* The player.
   *
   * The first audio copy in the product written in Norwegian: nothing in
   * ListenPanel, ClipPlayer, Spectrogram or Calendar is translated, because
   * those are staff instruments and staff read the English. This page is not
   * an instrument, and "Waking the hydrophone" in the middle of a Norwegian
   * page is the seam showing. */
  /* LYTTEPOST, NOT HYDROFON.
   *
   * The customer's word for the box in the water, on the customer's page. A
   * hydrophone is the transducer -- correct, and the word a supplier uses on an
   * invoice. What is deployed at Ljosoy is a place that listens: a recorder, an
   * uplink and a sensor on a mooring, and `lyttepost` is what somebody at the
   * farm calls that. Kept out of the fleet pages, where staff are genuinely
   * talking about the microphone and `Hydrofon` stays right. */
  'Listening post': 'Lyttepost',
  'The listening post': 'Lytteposten',
  'Listen now': 'Hør på nå',
  'Stop': 'Stopp',
  'Play': 'Spill av',
  'Pause': 'Pause',
  'Waking the listening post': 'Vekker lytteposten',
  'usually under two minutes': 'tar vanligvis under to minutter',
  'The listening post stopped sending after 30 minutes.':
    'Lytteposten sluttet å sende etter 30 minutter.',
  'Listen again': 'Hør på igjen',
  'Paused': 'På pause',
  'straight from the listening post': 'direkte fra lytteposten',
  'from the recording': 'fra opptaket',
  'Press the day below to start': 'Trykk på døgnet under for å starte',
  'Now': 'Nå',
  'Back to now': 'Tilbake til nå',
  /* The sensor switch on a node with a HydroMoth beside its hydrophone. The
     HydroMoth keeps its product name in both languages. */
  'Sensor': 'Sensor',
  'Not connected': 'Ikke tilkoblet',
  /* The archive, which is now a section with a name rather than a click on the
     word "Nå". "Hør tilbake" is a verb: it says what the thing is for, where
     "Opptak" would only say what it contains. */
  /* The day archive. "Hør tilbake" described the action; "Opptak" names the
     thing, which is what a section heading should do.
     Note this now differs from the `Recordings` key, which still reads Opptak
     and is the staff mission tab -- the two headings must not both be Opptak
     on the customer page, so the aside's clip list is `Clips` below. */
  'Listen back': 'Opptak',
  'Position in the recording': 'Posisjon i opptaket',
  /* The day strip's key. Capitalised, unlike the same facts in an hour's
     tooltip: a legend is a list of names and a tooltip is a sentence. */
  'You can listen': 'Du kan høre på',
  'On the listening post': 'På lytteposten',
  'Nothing recorded': 'Ingenting tatt opp',
  // The right-hand column. Two blocks, deliberately: see ListenAside.
  'None this day.': 'Ingen denne dagen.',
  'Where': 'Hvor',
  'deep': 'dypt',
  'Levels are estimated.': 'Nivåene er anslag.',
  'Decibels here are referenced to the sea, but they rest on the manufacturer’s figures for this equipment rather than on a measurement of it. Nothing has been checked against a reference meter yet, so read them as an indication of how loud something is rather than a figure to quote.':
    'Desibelen her viser til sjøen, men bygger på produsentens tall for utstyret '
    + 'og ikke på en måling av akkurat denne enheten. Ingenting er ennå '
    + 'kontrollert mot et referansemeter, så les dem som en pekepinn på hvor '
    + 'sterk lyden er, ikke som et tall å sitere.',
  'This listening post is calibrated, so the levels are decibels in the sea.':
    'Denne lytteposten er kalibrert, så nivåene er desibel i sjøen.',
  'Today': 'I dag',
  'Day before': 'Dagen før',
  'Day after': 'Dagen etter',
  'Show times in UTC': 'Vis klokkeslett i UTC',
  'of sound stored': 'lyd lagret',
  'press anywhere to listen back': 'trykk hvor som helst for å høre tilbake',
  // The unit, not the word: `18 t`, the way a clock face writes it.
  'h': 't',
  'No listening post is in the water at this farm.':
    'Ingen lyttepost står i sjøen på denne lokaliteten.',
  'Nothing from this day has reached us yet.':
    'Ingenting fra denne dagen har kommet fram ennå.',
  'Where in the day there is sound': 'Hvor på dagen det finnes lyd',
  'nothing recorded': 'ingenting tatt opp',
  'on the listening post, not here': 'på lytteposten, ikke her',
  'Recording': 'Opptak',
  'Record sound': 'Ta opp',
  'Stop and save': 'Stopp og lagre',
  'Saved in recordings': 'Lagret i opptak',
  'Saved on this device only.': 'Lagret bare på denne enheten.',
  'Lifted for listening': 'Løftet for lytting',

  /* Klipp: the spans of a recording a crew keeps and reads together.
   *
   * `Klipp` was already the word -- the aside beside the player has used it
   * for the clips somebody recorded themselves -- and the same word covers
   * both halves deliberately. A live recording is a file and a mark is a pair
   * of instants, which is a real difference to the player and no difference at
   * all to the person who kept it. Two words for that would be this product
   * explaining its storage model to a fish farmer.
   *
   * `Marker` rather than `Lagre` on the button that starts one: you are
   * marking where something begins, and it is not saved until the second press
   * and the note. Saying "lagre" at the first press would promise something
   * that has not happened yet. */
  'Mark a clip': 'Marker klipp',
  'Press again where it stops.': 'Trykk igjen der det slutter.',
  'Save clip': 'Lagre klipp',
  'Saving': 'Lagrer',
  'What did you hear?': 'Hva hørte du?',
  /* Said before somebody types, not after. That everyone at the farm reads
     the same list is the feature -- but it is also the one thing a person
     writing a note needs to know while they are writing it. */
  'Everyone at this farm will see it.': 'Alle på lokaliteten ser den.',
  'No note': 'Uten notat',
  'Add a note': 'Legg til notat',
  'Edit note': 'Endre notat',
  'Clip': 'Klipp',
  'Copy link': 'Kopier lenke',
  'Link copied': 'Lenke kopiert',
  'Could not copy. Copy the address bar instead.':
    'Fikk ikke kopiert. Kopier adressefeltet i stedet.',
  // A link that plays one clip for somebody with no account here.
  'Share with anyone': 'Del med hvem som helst',
  'Share with anyone…': 'Del med hvem som helst …',
  'Whoever opens the link sees': 'Den som åpner lenken ser',
  'The note, the sound and the timeline': 'Notatet, lyden og tidslinjen',
  'When it was recorded': 'Når det ble tatt opp',
  'The sound level in dB': 'Lydnivået i dB',
  'Not the farm or who kept it': 'Ikke anlegget eller hvem som lagret det',
  'Create link': 'Lag lenke',
  'Creating': 'Lager',
  'The link works for 90 days. You can stop it at any time.':
    'Lenken virker i 90 dager. Du kan stoppe den når som helst.',
  'The link': 'Lenken',
  'Could not copy. Select the link and copy it.': 'Fikk ikke kopiert. Merk lenken og kopier den.',
  'Works until': 'Virker til',
  'Opened': 'Åpnet',
  'opened': 'åpnet',
  'Not yet': 'Ikke ennå',
  'time': 'gang',
  'times': 'ganger',
  'Stop the link': 'Stopp lenken',
  'earlier link is out': 'tidligere lenke er ute',
  'earlier links are out': 'tidligere lenker er ute',
  'Stop it': 'Stopp den',
  'Stop them': 'Stopp dem',
  'Sound clip': 'Lydklipp',
  'The figure listens along': 'Figuren lytter med',
  'Sound level now': 'Lydnivå nå',
  'Low': 'Lav',
  'Mid': 'Mellom',
  'High': 'Høy',
  'Loudest': 'Høyest',
  'Colour scale': 'Fargeskala',
  'under': 'under',
  'over': 'over',
  '10 seconds back': '10 sekunder tilbake',
  '10 seconds forward': '10 sekunder fram',
  'Quiet here now': 'Stille her nå',
  'This link no longer works. The person who sent it has withdrawn it, or it has expired.':
    'Lenken virker ikke lenger. Den som sendte den har trukket den tilbake, eller den har utløpt.',
  'Ask the person who sent you the clip for a new link.':
    'Be om en ny lenke fra den som sendte deg klippet.',
  'Loading': 'Laster',
  'Link not found': 'Fant ikke lenken',
  'Check that the whole address was copied.': 'Sjekk at hele adressen ble kopiert.',
  // 'Remove' is already below, with the people editor's actions.
  'Remove this clip?': 'Fjerne klippet?',
  /* What is and is not destroyed, in the confirmation itself. A crew shares
     one list, so removing somebody's clip is a visible act -- and the fear it
     raises is that the recording goes with it, which it does not. */
  'It disappears from the farm’s list. The recording itself is not touched.':
    'Det forsvinner fra lokalitetens liste. Selve opptaket røres ikke.',
  /* A mark points at audio rather than owning a copy of it. If that audio is
     gone the honest answer is that it is gone, not a player that loads and
     produces silence. */
  'That recording is no longer in storage.':
    'Det opptaket ligger ikke lagret lenger.',
  // 'Save' and 'Cancel' are already above, from the site editors.

  /* The level meter, and the welfare ranges under it.
   *
   * Translated with the report's own Norwegian where the report has some --
   * these sentences are near-quotations of Havforskningsinstituttet's
   * sluttrapport for FHF 901744, and a loose retranslation of a hedged
   * statement loses the hedge, which here is the whole point. `bands.ts`
   * carries the original wording in a comment beside each figure. */
  'estimate': 'anslag',
  'Hatched above {n} dB — that is as loud as this listening post can measure.':
    'Skravert over {n} dB — så høyt kan lytteposten måle.',
  'The sound went above what this listening post can measure. It was at least this loud; how much louder is not known.':
    'Lyden gikk over det lytteposten kan måle. Den var minst så høy; hvor mye '
    + 'høyere vet vi ikke.',
  'Not assumed to cause significant negative effects.':
    'Antas ikke å gi betydelige negative konsekvenser.',
  'May have negative effects, depending on how long it lasts and how close the fish are.':
    'Kan gi negative konsekvenser avhengig av varighet og nærhet.',
  'Above the range the report describes for sustained sound. Its figures stop here and the next one it gives is about peak pressure.':
    'Over området rapporten beskriver for vedvarende lyd. Tallene stopper her, '
    + 'og det neste rapporten oppgir gjelder spisstrykk.',
  'Peak pressures above this are associated with hearing loss and physical injury.':
    'Spisstrykk over dette er forbundet med hørselstap og fysiske skader.',
  'Observation ranges from the Institute of Marine Research, not limits. The report states that thresholds for injury are not known.':
    'Observasjonsområder fra Havforskningsinstituttet, ikke grenseverdier. '
    + 'Rapporten sier at terskelverdier for skade ikke er kjent.',
  // The filter presets. There is deliberately no "Foringsautomat" here --
  // bands.ts says at length why a guessed band is worse than no band.
  'Filter': 'Filter',
  'All': 'Alt',
  'Infrasound': 'Infralyd',
  'The fish': 'Fisken',
  'Feeder': 'Fôringsautomat',
  'Feeding': 'Fôring',
  'Own': 'Egne',
  'drag the edges': 'dra i kantene',
  // Said at the control rather than behind the expander. A band nobody can
  // hear must announce that where it is pressed.
  'Below hearing — watch the strip.': 'Under hørsel — se på båndet.',
  'the top is not in this stream': 'toppen finnes ikke i denne strømmen',
  'All the sound the hydrophone hears': 'All lyden hydrofonen hører',
  'Sound from {lo} to {hi}': 'Lyd fra {lo} til {hi}',
  'This browser cannot analyse a live stream, so the filters are not available here. The sound is unchanged, and yesterday still filters normally.':
    'Denne nettleseren kan ikke analysere direktelyd, så filtrene er ikke '
    + 'tilgjengelige her. Lyden er den samme, og opptak fra tidligere dager '
    + 'kan filtreres som vanlig.',
  'Hear nothing? Check the silent switch on the side of the phone.':
    'Hører du ingenting? Sjekk lydbryteren på siden av telefonen.',

  'Over grensen': 'Over grensen',
  'Nærmer seg': 'Nærmer seg',
  'Nærmer seg grensen': 'Nærmer seg grensen',
  'Under grensen': 'Under grensen',
  'Ikke talt': 'Ikke talt',
  'of': 'av',
  /* The two fish. `Teoretisk` is the word the model's own payload uses for
     itself, and it names what the model is for; `uverifisert` names a defect it
     does not have. A stage-dynamics model is not a broken empirical one. */
  'Measured': 'Målt',
  'Theoretical': 'Teoretisk',
  'counted at the farm': 'talt på lokaliteten',
  'reported to the register': 'rapportert til registeret',
  'chance of breaching the limit within': 'sjanse for å bryte grensen innen',
  'weeks, if nothing is done': 'uker, hvis ingenting gjøres',
  'Forecast band against the limit': 'Prognosebånd mot grensen',
  'limit': 'grense',
  'The band is where a count usually lands; the dashed and dotted lines are 90% and 99%. Under each week: the chance of having breached by then.':
    'Båndet er der en telling som regel havner; stiplet og prikket linje er 90 % og 99 %. Under hver uke: sjansen for å ha brutt grensen innen da.',
  'This farm is fallow, so there is nothing to forecast.':
    'Denne lokaliteten er brakklagt, så det er ingenting å varsle.',
  'How to read the forecast': 'Slik leses varselet',
  'weeks since the last count reached the model':
    'uker siden siste telling nådde modellen',
  'Built on your own count from': 'Bygget på deres egen telling fra',
  'The model would not take the temperature you entered:':
    'Modellen tok ikke temperaturen dere la inn:',
  'The weeks between the register and your count carry no treatment record:':
    'Ukene mellom registeret og tellingen deres mangler behandlingsdata:',
  'Add a lice count': 'Legg til lusetelling',
  'Reported lice from': 'Rapportert lusetall fra',
  'to': 'til',
  'Most likely goes from': 'Mest sannsynlig går fra',
  'The limit is': 'Grensen er',
  'Overall chance of breaching it is': 'Samlet sjanse for å bryte den er',
  'Sessile': 'Fastsittende',
  'Mobile': 'Bevegelige',
  'Adult female': 'Voksne hunnlus',
  'Counted on': 'Talt den',
  'Fish counted': 'Antall fisk talt',
  'Leave a stage blank if it was not counted. Blank is not zero.':
    'La et stadium stå tomt om det ikke ble talt. Tomt er ikke null.',
  'Counted': 'Talt',
  'Fish': 'Fisk',
  'By': 'Av',

  /* Entering a count. Terminology is the register's and phoniq-insight's, not
     invented here: voksne hunnlus, bevegelige, fastsittende, telling, avlusing,
     and `grensen` rather than `grensa`. */
  'Adult female per fish': 'Voksne hunnlus per fisk',
  'Note': 'Notat',
  'Optional. What else happened that week.':
    'Valgfritt. Hva annet som skjedde den uka.',
  'counted in cages 3 and 5': 'talt i merd 3 og 5',
  'Without this the model assumes 100 fish. Ten is the legal minimum, and not enough to tell 0,10 from 0,20 on one count.':
    'Uten dette regner modellen med 100 fisk. Ti er lovens minimum, og ikke nok til å skille 0,10 fra 0,20 på én telling.',
  'Mobile and sessile lice become adult females in two to three weeks. Without them the forecast is measurably worse.':
    'Bevegelige og fastsittende lus blir voksne hunnlus om to–tre uker. Uten dem varsler modellen målbart dårligere.',
  'Correct the count from': 'Rett opp tellingen fra',
  'There is already a count for this day': 'Det finnes allerede en telling denne dagen',
  'entered by': 'lagt inn av',
  'Saving replaces it. The previous numbers are kept in the record.':
    'Lagring erstatter den. De forrige tallene beholdes i sporet.',
  "This day falls inside the register's own window, which closes":
    'Denne dagen ligger innenfor registerets eget vindu, som lukker',
  'The count is stored, but the forecast keeps building on the register.':
    'Tellingen lagres, men varselet bygger fortsatt på registeret.',
  'is over the limit of': 'er over grensen på',
  'for week': 'for uke',
  'Saving the count…': 'Lagrer tellingen …',
  'Save the correction': 'Lagre rettelsen',
  'Save and re-run the forecast': 'Lagre og kjør varselet på nytt',
  'Running the model again': 'Kjører modellen på nytt',
  'If you deloused': 'Hvis dere avluser',
  'choose a week': 'velg uke',
  'this week': 'denne uken',
  'next week': 'neste uke',
  'Asking the model': 'Spør modellen',
  'Assumes the delousing works.': 'Forutsetter at avlusingen virker.',
  'Without delousing': 'Uten avlusing',
  'With delousing': 'Med avlusing',
  'Your count is saved.': 'Tellingen er lagret.',
  'The forecast could not be fetched just now.':
    'Varselet kunne ikke hentes akkurat nå.',
  'Try again': 'Prøv igjen',
  'Have you counted since?': 'Har dere talt siden sist?',
  'The forecast is built on': 'Varselet bygger på',
  'a count from': 'en telling fra',
  "the register's figure from": 'registerets tall fra',

  /* The delousing question. It exists because the forecast's own claim is
     conditional -- "om dere ikke avluser før det" -- so a count taken after a
     delousing measures a different world from the one that was forecast. */
  'Have you deloused': 'Er det avluset',
  'since': 'siden',
  'No': 'Nei',
  'Yes': 'Ja',
  'Deloused on': 'Avluset den',
  'Method': 'Metode',
  'Coverage': 'Dekning',
  'All cages': 'Alle merder',
  'Some cages': 'Noen merder',
  'Delousings on record': 'Avlusinger i sporet',

  /* How the forecast landed. */
  'Close': 'Lukk',
  'eight times out of ten between': 'åtte av ti ganger mellom',
  'and': 'og',

  /* Entering a count, one question at a time. The questions are asked, not
     labelled -- "Når talte dere?" rather than "Dato" -- because a question has
     an obvious answer and a label has to be interpreted first. */
  'When did you count?': 'Når talte dere?',
  'Have you deloused since': 'Er det avluset siden',
  'The forecast only says what happens if you do not. If you did, it is not a test of it.':
    'Varselet sier bare hva som skjer om dere lar være. Har dere avluset, er tellingen ikke en test av det.',
  'When did you delouse?': 'Når avluset dere?',
  'Which method?': 'Hvilken metode?',
  'Each one knocks the three stages down differently, and the model knows by how much.':
    'Hver metode slår ned de tre stadiene ulikt, og modellen vet hvor mye.',
  'How much of the site?': 'Hvor mye av anlegget?',
  'A partial delousing that halves the average is not the same event as one that empties it.':
    'En delvis avlusing som halverer snittet er ikke samme hendelse som en som tømmer det.',
  'How many adult female lice per fish?': 'Hvor mange voksne hunnlus per fisk?',
  'And mobile lice?': 'Og bevegelige lus?',
  'Blank if you did not count them. Blank is not zero.':
    'La stå tomt om dere ikke talte dem. Tomt er ikke null.',
  'And sessile lice?': 'Og fastsittende lus?',
  'These become adult females in two to three weeks. Without them the forecast is measurably worse.':
    'Disse blir voksne hunnlus om to–tre uker. Uten dem varsler modellen målbart dårligere.',
  'How many fish did you count?': 'Hvor mange fisk talte dere?',
  'Without this the model assumes 100. Ten is the legal minimum, and not enough to tell 0,10 from 0,20 on one count.':
    'Uten dette regner modellen med 100. Ti er lovens minimum, og ikke nok til å skille 0,10 fra 0,20 på én telling.',
  'Anything worth noting?': 'Noe verdt å notere?',
  'How warm is the sea?': 'Hvor varm er sjøen?',
  'At three metres, where the lice are counted.':
    'Ved tre meter, der lusa telles.',
  'Sea temperature': 'Sjøtemperatur',
  'Next': 'Neste',
  'Skip': 'Hopp over',
  'or press': 'eller trykk',
  'Back': 'Tilbake',
  'Does this look right?': 'Ser dette riktig ut?',
  'Save this correction?': 'Lagre denne rettelsen?',
  'Tap a line to change it.': 'Trykk på en linje for å endre den.',
  'not counted': 'ikke talt',
  'Deloused': 'Avluset',
  'Discard this count?': 'Forkaste denne tellingen?',
  'Nothing has been saved yet.': 'Ingenting er lagret ennå.',
  'Discard it': 'Forkast den',
  'Keep going': 'Fortsett',

  /* The last screen. It rewards the forecast having been right and the habit
     of counting -- never a low number, which would be paying people in small
     dopamine to under-report a figure a treatment order rests on. */
  'weeks in a row': 'uker på rad',
  'week counted': 'uke talt',
  'See the forecast': 'Se varselet',

  /* The record, and taking one back. */
  'Your counts': 'Deres tellinger',
  'Week': 'Uke',
  'Forecast': 'Varsel',
  'Count': 'Telling',
  'Correct it': 'Rett opp',
  'Withdraw it': 'Trekk tilbake',
  'Withdraw it?': 'Trekke tilbake?',
  'It stops counting, stops anchoring the model, and frees that day again. The record that it was entered is kept.':
    'Den slutter å telle, slutter å forankre modellen, og frigjør dagen igjen. Sporet etter at den ble lagt inn beholdes.',

  'Register window': 'Registerets vindu',
  'Forecast anchored on': 'Prognose forankret på',
  // 'Model' is already above, from the calibration page.
  'No forecast: showing reported figures only':
    'Ingen prognose: viser kun rapporterte tall',
  'the model was unreachable, this is the last answer':
    'modellen var utilgjengelig, dette er siste svar',
  'View as this customer': 'Se som denne kunden',
  'Viewing as': 'Ser som',
  'read only. Your own farms and menus are hidden until you leave.':
    'kun lesing. Dine egne anlegg og menyer er skjult til du går ut.',
  'Back to Phoniq': 'Tilbake til Phoniq',
  'Optional. Leave it blank if the company has none.':
    'Valgfritt. La stå tomt om selskapet ikke har et.',
  'Typed at commissioning as:': 'Skrevet inn ved oppsett som:',
  'Farms with no owner': 'Lokaliteter uten eier',
  'Give to': 'Gi til',
  'typed as': 'skrevet som',
  'No area': 'Uten område',
  'New area': 'Nytt område',
  'Add area': 'Legg til område',
  'Top level': 'Øverste nivå',
  'Inside': 'Inne i',
  'Add farm': 'Legg til lokalitet',
  'added.': 'er lagt til.',
  'A farm is added from the locality register. Search for it by name or number.':
    'En lokalitet legges til fra lokalitetsregisteret. Søk etter navn eller nummer.',
  'An area is not a farm: add farms in the Farms card above.':
    'Et område er ikke en lokalitet: legg til lokaliteter i kortet over.',
  'Invite somebody': 'Inviter noen',
  'Send invitation': 'Send invitasjon',
  'Invitation sent again.': 'Invitasjonen er sendt på nytt.',
  'Resend': 'Send på nytt',
  'Remove': 'Fjern',
  'Remove access for': 'Fjern tilgang for',
  'Not set up yet': 'Ikke satt opp ennå',
  'Never signed in': 'Aldri logget inn',
  'Nobody yet': 'Ingen ennå',
  'Products': 'Produkter',
  'Grant a product': 'Gi et produkt',
  'Grant': 'Gi',
  'Revoke': 'Trekk tilbake',
  'Nothing granted': 'Ingenting gitt',
  'Every farm': 'Alle lokaliteter',
  'Every farm this company owns': 'Alle lokaliteter dette selskapet eier',
  'Applies to': 'Gjelder',
  'Included': 'Inkludert',
  'base only': 'kun basis',
  'until': 'til',
  // 'Cancel' is already above, from the site editors.
  'Create': 'Opprett',
  'Locality': 'Lokalitet',
  'Status': 'Status',
  'Person': 'Person',
  'Role': 'Rolle',
  'Signing out…': 'Logger ut…',



  /* ------------------------------------------------------- field devices
   *
   * The one part of this product read by somebody who did not choose it, is
   * not paid to use it, and is holding a phone in wet gloves. So: no nouns
   * from the data model, no "enrollment", no "credential", no "session".
   * "Koble til" is what you do to a charger. The register itself is
   * "telling", which is the word already used on the walkway and in the
   * regulation.
   */
  'Connect this device': 'Koble til denne enheten',
  'Type the code you were given. There is nothing else to set up.':
    'Skriv koden du fikk. Det er ikke noe mer å sette opp.',
  'Code': 'Kode',
  'Twelve characters. Upper and lower case are the same, and dashes do not matter.':
    'Tolv tegn. Store og små bokstaver er det samme, og bindestreker spiller ingen rolle.',
  'Connecting…': 'Kobler til…',
  'Connect': 'Koble til',
  'This device stays connected. You will not be asked again.':
    'Enheten forblir tilkoblet. Du blir ikke spurt igjen.',
  // Deliberately one sentence for unknown, expired, revoked and used up: the
  // receiver answers identically for all four, and the screen must not invent
  // a distinction it refused to make.
  'That code is not in use. Check it, or ask for a new one.':
    'Den koden er ikke i bruk. Sjekk den, eller be om en ny.',
  'That code points at something that is no longer there.':
    'Den koden peker på noe som ikke finnes lenger.',
  'Too many tries from here. Wait a few minutes.':
    'For mange forsøk herfra. Vent noen minutter.',
  'A code is twelve characters.': 'En kode er tolv tegn.',
  'Could not reach the receiver.': 'Fikk ikke kontakt med mottakeren.',
  'The person who was signed in on this browser has been signed out.':
    'Personen som var logget inn i denne nettleseren er logget ut.',
  'Connected to': 'Koblet til',
  'Connected.': 'Tilkoblet.',

  'What is this device called?': 'Hva heter denne enheten?',
  'Merd 3, or your name': 'Merd 3, eller navnet ditt',
  'So your counts can be told apart from the next tablet’s. Change it any time.':
    'Så tellingene dine kan skilles fra nabobrettets. Kan endres når som helst.',
  'Start counting': 'Begynn å telle',
  'Name this device': 'Gi enheten et navn',
  'Change the name on this device': 'Endre navnet på denne enheten',
  'Everything you enter is recorded under this name.':
    'Alt du registrerer blir ført under dette navnet.',

  'Which farm?': 'Hvilken lokalitet?',
  'Another farm': 'Annen lokalitet',
  'Register a count': 'Registrer telling',
  'Recent counts': 'Siste tellinger',
  'Nothing counted here yet.': 'Ingen tellinger her ennå.',
  'adult female per fish': 'voksne hunnlus per fisk',
  'Take back': 'Trekk tilbake',
  'Yes, take it back': 'Ja, trekk den tilbake',
  'Taking it back…': 'Trekker tilbake…',
  'This device is not connected to a farm. Ask for a new code.':
    'Denne enheten er ikke koblet til en lokalitet. Be om en ny kode.',
  'This device may not register counts. Ask for a different code.':
    'Denne enheten kan ikke registrere tellinger. Be om en annen kode.',

  'Enter a new code': 'Bytt kode',
  'Disconnect this device': 'Meld ut denne enheten',
  'Yes, disconnect it': 'Ja, meld den ut',
  'This device will be disconnected. Counts already entered stay where they are, still under this name.':
    'Enheten blir meldt ut. Tellinger som allerede er registrert blir stående, fortsatt under dette navnet.',

  /* --------------------------------------------------- devices, from a desk */
  'Devices': 'Enheter',

  /* Merdene. Regelverket er skrevet per merd og rapporten per lokalitet, så
     dette er beviset og uketabellen er protokollen. */
  'By pen': 'Per merd',
  'Pen': 'Merd',
  'pens': 'merder',
  'The site': 'Anlegget',
  'Take it back': 'Trekk tilbake',
  'Counted fish by fish': 'Talt fisk for fisk',
  'skipped:weather': 'ikke talt \u2014 v\u00e6r',
  'skipped:treatment': 'ikke talt \u2014 avlusing',
  'skipped:handling': 'ikke talt \u2014 h\u00e5ndtering',
  'skipped:no_fish': 'ikke talt \u2014 tom merd',
  'skipped:equipment': 'ikke talt \u2014 utstyr',
  'skipped:other': 'ikke talt',
  'The pens gave different numbers of fish, so the site figure is weighted by fish counted — not the average of the pen averages.':
    'Merdene ga ulikt antall fisk, s\u00e5 anleggstallet er vektet etter fisk talt \u2014 ikke snittet av merdsnittene.',
  'is what puts the site over.': 'er det som tar anlegget over.',
  'Treating only that one brings the site to': 'Avluser dere bare den, havner anlegget p\u00e5',
  'Our arithmetic on your own counts, not a forecast — and §9 wants it documented before you treat one pen instead of all of them.':
    'V\u00e5r regning p\u00e5 deres egne tellinger, ikke et varsel \u2014 og \u00a79 vil ha det dokumentert f\u00f8r dere avluser \u00e9n merd i stedet for alle.',

  /* Anleggskartet. Merdene har alltid vært en liste; her får de en posisjon,
     og det er posisjonen som gjør at "merd 3 er alltid verst" blir en setning
     noen kan se i stedet for en de må tro på. */
  'Set up the pens': 'Sett opp merder',
  'Set up': 'Sett opp',
  'Drag onto the map': 'Dra ut på kartet',
  'Round pen': 'Rund merd',
  'Square pen': 'Firkantet merd',
  'Feed barge': 'F\u00f4rfl\u00e5te',
  'Now tap the map where it goes.': 'Trykk der den skal ligge.',
  'pens have no position yet.': 'merder mangler posisjon.',
  'Lay them out': 'Legg dem ut',
  'Two rows around the farm, to drag from. Nothing is saved until you save.':
    'To rader rundt anlegget, \u00e5 dra videre fra. Ingenting lagres f\u00f8r du lagrer.',
  'This farm has no position yet — drop one pen first.':
    'Anlegget har ingen posisjon enn\u00e5 \u2014 slipp ut \u00e9n merd f\u00f8rst.',
  'Round': 'Rund',
  'Square': 'Firkantet',
  'Circumference (m)': 'Omkrets (m)',
  'Depth (m)': 'Dybde (m)',
  'Length (m)': 'Lengde (m)',
  'Width (m)': 'Bredde (m)',
  'Turned': 'Dreid',
  'Remove this pen': 'Fjern denne merden',
  'It is retired, not deleted. Counts already made under it stay where they are.':
    'Den pensjoneres, ikke slettes. Tellinger som allerede er gjort i den blir st\u00e5ende.',
  'Tap a pen to rename or resize it. Drag it to move it.':
    'Trykk p\u00e5 en merd for \u00e5 endre navn eller st\u00f8rrelse. Dra den for \u00e5 flytte den.',
  /* Kundesiden: kartet er produktet, tabellen er beviset og uketabellen er
     protokollen. Tre horisonter som ikke skal blandes. */
  'Count this pen': 'Tell denne merden',
  'No count per pen for this day. Every pen is showing the farm figure,':
    'Ingen telling per merd denne dagen. Alle merdene viser anleggets tall,',
  "The farm's figure, not this pen's.":
    'Anleggets tall, ikke denne merdens.',
  "The farm's figure, spread across the pens":
    'Anleggets tall, lagt ut p\u00e5 merdene',
  'Every week, by pen': 'Uke for uke, per merd',
  'Stage': 'Stadium',

  /* Fisken. Merken er fisken og ringen er stedet -- man drar det som flytter
     seg. "Utsetting" er allerede brukt om hydrofoner, så smoltutsett må hete
     noe annet. */
  'Move fish': 'Flytt fisk',
  'To the slaughterhouse': 'Til slakteri',
  'went to the slaughterhouse': 'gikk til slakteri',
  'Slaughterhouse': 'Slakteri',
  'Delousing': 'Avlusing',
  'Put fish in': 'Sett inn fisk',
  'Put them in': 'Sett dem inn',
  'What are they called?': 'Hva heter de?',
  'From which hatchery?': 'Fra hvilket settefiskanlegg?',
  'method:termisk': 'termisk',
  'method:mekanisk': 'mekanisk',
  'method:ferskvann': 'ferskvann',
  'method:kaldtvann': 'kaldtvann',
  'method:medikament': 'medikament',
  'Merge them': 'Sl\u00e5 sammen',
  'Move those out': 'Flytt dem ut',
  'The two become one population.': 'De to blir \u00e9n gruppe.',
  '%s comes out and waits for a pen. You place it next.':
    '%s kommer ut og venter p\u00e5 en merd. Du plasserer den etterp\u00e5.',
  'is standing in no pen. Tap the pen it went to.':
    'st\u00e5r uten merd. Trykk p\u00e5 merden de gikk til.',
  'Now tap the pen it applies to.': 'Trykk p\u00e5 merden det gjelder.',
  'Now tap the pen they go into.': 'Trykk p\u00e5 merden de skal i.',
  'Empty the pen': 'T\u00f8m merden',
  'Record the delousing': 'Registrer avlusingen',
  'The pen is emptied. Counts already made in it stay in the record, and the generation is dated when its last pen goes.':
    'Merden t\u00f8mmes. Tellinger som allerede er gjort i den blir st\u00e5ende, og generasjonen dateres n\u00e5r siste merd g\u00e5r.',
  'When?': 'N\u00e5r?',
  'Yesterday': 'I g\u00e5r',
  'How much?': 'Hvor mye?',
  'All of it': 'Alt',
  'Part of it': 'Deler av',
  'number of fish': 'antall fisk',
  'Leave it blank if you do not know.': 'La det st\u00e5 tomt om du ikke vet.',
  'Why?': 'Hvorfor?',
  'optional': 'valgfritt',
  'Move': 'Flytt',
  'why:treatment': 'avlusing',
  'why:grading': 'sortering',
  'why:merge': 'sammensl\u00e5ing',
  'why:harvest': 'slakting',
  'why:move': 'flytting',
  'why:split': 'oppdeling',
  'why:stocking': 'innsett',
  'why:other': 'annet',
  'already holds': 'har allerede',
  'These fish will be merged into it.': 'Disse fiskene blir sl\u00e5tt sammen med den.',
  'Is this the fish standing here now?': 'Er dette fisken som st\u00e5r her n\u00e5?',
  'Smolt from': 'Settefisk fra',
  'Fish from': 'Fisk fra',
  'carried by': 'levert av',
  'Yes, that is them': 'Ja, det er den',
  'From the register. You can correct it afterwards — every pen gets the group, and you drag from there.':
    'Fra registeret. Du kan rette det etterp\u00e5 \u2014 alle merdene f\u00e5r gruppen, og s\u00e5 drar du derfra.',
  'movements on record': 'flyttinger registrert',
  'put in': 'satt i',
  'Tap a fish to pick it up, then tap where it goes. Dragging works too.':
    'Trykk p\u00e5 en fisk for \u00e5 ta den opp, og s\u00e5 p\u00e5 der den skal. Du kan dra den ogs\u00e5.',
  'is picked up from pen': 'er tatt opp fra merd',
  'empty': 'tom',
  'That is not a number.': 'Det er ikke et tall.',
  'No fish in this pen that week.': 'Ingen fisk i denne merden den uken.',
  'move recorded.': 'flytting registrert.',
  'moves recorded.': 'flyttinger registrert.',
  'Tap another fish to move more.': 'Trykk p\u00e5 en fisk til for \u00e5 flytte mer.',
  'Finish': 'Avslutt',
  'No fish in this pen.': 'Ingen fisk i denne merden.',
  'Counted in pen': 'Talt i merd',
  'Tap the pen they go to, or one of the three above.':
    'Trykk p\u00e5 merden de skal til, eller p\u00e5 en av de tre over.',
  'fish': 'fisk',
  'no data': 'ingen data',
  'register': 'register',
  'No limit for this stage — height is a share of the highest figure in the table':
    'Ingen grense for dette stadiet \u2014 h\u00f8yden er andel av det h\u00f8yeste tallet i tabellen',
  'No week has been counted per pen yet, so every row shows the farm figure.':
    'Ingen uke er talt per merd enn\u00e5, s\u00e5 alle radene viser anleggets tall.',
  'retired': 'pensjonert',
  'History': 'Historikk',
  'No forecast for this farm.': 'Ingen varsel for dette anlegget.',
  'Under half the limit': 'Under halve grensen',
  'Half to the limit': 'Halve grensen til grensen',
  'At or over the limit': 'P\u00e5 eller over grensen',
  'Not counted': 'Ikke talt',
  'the locality, not the pen': 'gjelder lokaliteten, ikke merden',
  'delousing': 'avlusing',
  'Which boat took them?': 'Hvilken b\u00e5t hentet dem?',
  '%s has stood here since %s.': '%s har st\u00e5tt her siden %s.',
  'Looking for boats\u2026': 'Leter etter b\u00e5ter\u2026',
  'Unnamed vessel': 'Ukjent fart\u00f8y',
  'h alongside': 't ved anlegget',
  'the register does not say why it came': 'registeret sier ikke hvorfor den kom',
  'lay here, then went to a slaughterhouse': 'l\u00e5 her, gikk s\u00e5 til slakteri',
  'Delousing recorded in pen %s.': 'Avlusing registrert i merd %s.',
  'Pen %s emptied to the slaughterhouse.': 'Merd %s t\u00f8mt til slakteriet.',
  'Fish put into pen %s.': 'Fisk satt inn i merd %s.',
  'No pen held fish on this day.': 'Ingen merd hadde fisk denne dagen.',
  'One pen held no fish on this day and is not on the sheet.': '\u00c9n merd hadde ikke fisk denne dagen og st\u00e5r ikke p\u00e5 arket.',
  '%s pens held no fish on this day and are not on the sheet.': '%s merder hadde ikke fisk denne dagen og st\u00e5r ikke p\u00e5 arket.',
  'event:harvest_suspected': 'trolig slakting',
  'Or set the date yourself below, if the boat is not listed.': 'Eller sett datoen selv under, hvis b\u00e5ten ikke st\u00e5r i lista.',
  'event:delousing': 'avlusing',
  'event:delousing_suspected': 'trolig avlusing',
  'event:activity_unknown': 'ukjent aktivitet',
  'Fetching the cycle': 'Henter syklusen',
  'The cycle could not be fetched.': 'Klarte ikke hente syklusen.',
  'No weeks on record for this farm yet.': 'Ingen uker registrert p\u00e5 dette anlegget enn\u00e5.',
  'This farm is not drawn yet.': 'Anlegget er ikke tegnet inn enn\u00e5.',
  'pens are not on the map yet.': 'merder er ikke plassert p\u00e5 kartet.',
  'Fish per pen': 'Fisk per merd',
  'This farm has no pens registered yet.':
    'Anlegget har ingen merder registrert enn\u00e5.',
  'other': 'annet',
  'pens are still blank. Vedlegg 1 asks for every one.':
    'merder st\u00e5r fortsatt tomme. Vedlegg 1 ber om alle.',
  'The whole array': 'Hele anlegget',
  'Drag \u271b on the map to move all of it at once.':
    'Dra \u271b p\u00e5 kartet for \u00e5 flytte hele anlegget.',
  'Turn': 'Drei',
  'Spacing': 'Avstand',
  'Or drag the small handle on the ring to size it against the picture.':
    'Eller dra i det lille h\u00e5ndtaket p\u00e5 ringen for \u00e5 m\u00e5le den mot bildet.',
  'Give every pen this size': 'Gi alle merdene denne st\u00f8rrelsen',
  'Satellite': 'Satellitt',
  'Nautical chart': 'Sj\u00f8kart',
  'Map': 'Kart',
  'Full screen': 'Full skjerm',
  'The cycle': 'Syklusen',
  'forecast': 'varsel',
  'Put them back in pen': 'Sett dem tilbake i merd',
  'Leave pen': 'La merd',
  'The move is withdrawn.': 'Flyttingen trekkes tilbake.',
  'goes back to pen': 'går tilbake til merd',
  'This is not a move — it is where the fish were put in.':
    'Dette er ikke en flytting — det er der fisken ble satt inn.',
  'will hold no fish at all, and its counts will belong to nobody.':
    'vil da ikke ha fisk i det hele tatt, og tellingene der tilhører ingen.',
  'Under the limit all': 'Under grensen alle',
  'weeks ahead': 'ukene framover',
  'Hide the pens': 'Skjul merdene',
  'Drag along the weeks behind now and the map follows. Ahead of the line is the forecast, which is the locality and not the pen.':
    'Dra langs ukene bak nå, så følger kartet. Foran streken er varselet, som gjelder lokaliteten og ikke merden.',
  'Around now': 'Rundt nå',
  'Over the limit in': 'Over grensen om',
  'week': 'uke',
  'weeks': 'uker',
  'at': 'på',
  'the limit': 'grensen',
  'The whole cycle': 'Hele syklusen',
  'worst pen this week': 'verste merd denne uka',
  'Show the figures': 'Vis tallene',
  'lice per ten fish': 'lus per ti fisk',
  'the limit is': 'grensen er',
  'per fish': 'per fisk',
  'tightened': 'strammere grense',
  'Looking back': 'Ser bakover',
  'You are looking at an earlier week.': 'Du ser på en tidligere uke.',
  'Nothing can be changed from here — a move recorded now would be filed on the wrong week.':
    'Ingenting kan endres herfra — en flytting registrert nå ville blitt ført på feil uke.',
  'Open the map full screen': 'Åpne kartet i full skjerm',
  'Hide the panel': 'Skjul panelet',
  'Show the panel': 'Vis panelet',
  'Save the layout': 'Lagre kartet',
  'Saved.': 'Lagret.',
  'The map needs a Mapbox token in this build.':
    'Kartet mangler en Mapbox-n\u00f8kkel i denne utgaven.',
  'The map could not load.': 'Kartet lot seg ikke laste.',
  'Where the pens are': 'Hvor merdene ligger',
  'Nobody knows a latitude. They know it is the one at the mouth, second from the barge.':
    'Ingen kan en breddegrad. De vet at det er den ytterst, nummer to fra fl\u00e5ten.',

  /* Å slippe en enhet inn. Koden er en adresse og ikke en hemmelighet -- hver
     lokalitet et norsk selskap eier står i et offentlig register med navn, så
     noe kort nok til å huskes er kort nok til å gjettes. Det er godkjenningen
     som er sperren, og ordene her må si det uten å be noen lese en forklaring. */
  'Waiting to be let in': 'Venter på å slippe inn',
  'Your manager has been asked to approve this device for':
    'Lederen din er spurt om å godkjenne denne enheten for',
  'It usually takes a moment.': 'Det tar som regel et øyeblikk.',
  'You can leave this screen open. It will start on its own.':
    'Du kan la denne skjermen stå åpen. Den starter av seg selv.',
  'Nothing is registered and nothing is visible until then.':
    'Ingenting registreres og ingenting vises før det.',
  'Check again': 'Sjekk på nytt',

  'Let this device in?': 'Slippe inn denne enheten?',
  'Give this device wider access?': 'Gi denne enheten videre tilgang?',
  'Somebody used your connection code.': 'Noen har brukt tilkoblingskoden deres.',
  'It is already counting for you and has asked for more.':
    'Den teller allerede for dere, og har bedt om mer.',
  'Device': 'Enhet',
  'Wants to count at': 'Vil telle på',
  'Code used': 'Kode brukt',
  'Coming from': 'Kommer fra',
  'Asked': 'Spurte',
  'unnamed': 'uten navn',
  'Let it in': 'Slipp den inn',
  'Turn it away': 'Avvis den',
  'One moment…': 'Et øyeblikk…',
  'Looking it up…': 'Henter…',
  'Let in.': 'Sluppet inn.',
  'Turned away.': 'Avvist.',
  'It can start counting straight away — the screen it is waiting on will move on by itself.':
    'Den kan begynne å telle med en gang — skjermen den venter på går videre av seg selv.',
  'It stops at its very next request. Nothing it had is kept.':
    'Den stopper ved aller neste forespørsel. Ingenting den hadde beholdes.',
  'See the devices': 'Se enhetene',
  'That link is incomplete.': 'Den lenken er ufullstendig.',
  'That device has already been dealt with.': 'Den enheten er allerede behandlet.',
  'That link has expired or has already been used. You can still find the device on the Devices page.':
    'Lenken er utløpt eller allerede brukt. Du finner enheten fortsatt på Enheter-siden.',
  'If you were not expecting this, turn it away. The code is meant to be easy to remember, so it is not a secret — this is.':
    'Venter du ikke på dette, avvis den. Koden skal være lett å huske, og er derfor ingen hemmelighet — det er dette som er sperren.',

  'Approve': 'Godkjenn',
  'Decline': 'Avvis',
  'Asking to connect to': 'Ber om å koble seg til',
  'Asking for wider access to': 'Ber om videre tilgang til',
  'from': 'fra',
  'this company': 'dette selskapet',
  'approved by': 'godkjent av',
  'approved from the email': 'godkjent fra e-posten',
  'connected before approval was required': 'koblet til før godkjenning ble innført',
  'let in by': 'sluppet inn av',
  'a manager': 'en leder',
  'a registered network': 'et registrert nettverk',

  /* Nettverkene. Avsnittet er skrevet for noen som kommer til å bli skuffet,
     så det sier hva det ER før det sier hva det gjør: offentlig IP-adresse,
     ikke navnet på nettverket, og virker ikke på mobildata. */
  'Your networks': 'Nettverkene deres',
  'A device connecting from one of these is let in immediately, with nobody asked. Afterwards it works from anywhere.':
    'En enhet som kobler seg til fra ett av disse slipper rett inn, uten at noen blir spurt. Etterpå virker den overalt.',
  'Add the one I am on': 'Legg til det jeg er på nå',
  'You are already on a registered network': 'Du er allerede på et registrert nettverk',
  'The office': 'Kontoret',
  'Unnamed network': 'Nettverk uten navn',
  'you are on this one': 'du er på dette',
  'no devices yet': 'ingen enheter ennå',
  'No device came in this way.': 'Ingen enhet kom inn denne veien.',
  'Remove it': 'Fjern det',
  'None yet. Without one, every new device waits for you to approve it — which is fine, and is the safe default.':
    'Ingen ennå. Uten et nettverk venter hver ny enhet på at du godkjenner den — det er helt greit, og er det trygge utgangspunktet.',
  'This is the internet address you appear from, not the wifi name — a web page is never told which network it is on. It works for an office or a base with a fixed line. It does not work over mobile data, where a phone at the farm and a phone in Oslo look the same from here.':
    'Dette er internettadressen dere ser ut til å komme fra, ikke navnet på nettverket — en nettside får aldri vite hvilket nettverk den er på. Det virker for et kontor eller en base med fast linje. Det virker ikke på mobildata, der en telefon på anlegget og en telefon i Oslo ser like ut herfra.',

  /* Koden, valgt i stedet for generert. */
  'The code': 'Koden',
  'Short and easy to say. Anyone who has it still has to be let in by you, or be on one of your networks.':
    'Kort og lett å si. Den som har den må fortsatt slippes inn av deg, eller være på ett av nettverkene deres.',
  'Letters I and O are read as 1 and 0, so this code is':
    'Bokstavene I og O leses som 1 og 0, så koden blir',
  'Here it is.': 'Her er den.',
  'It is in the list below whenever you need it again — it is not a password, and anybody using it still has to be approved by you.':
    'Den ligger i lista under når du trenger den igjen — den er ikke et passord, og den som bruker den må fortsatt godkjennes av deg.',
  'Anyone can use it, as many times as they like.':
    'Hvem som helst kan bruke den, så mange ganger de vil.',
  'Upper and lower case are the same, and dashes do not matter.':
    'Store og små bokstaver er det samme, og bindestreker spiller ingen rolle.',
  'So your counts can be told apart from the next tablet’s, and so your manager knows what they are letting in.':
    'Så tellingene dine kan skilles fra neste nettbrett sine, og så lederen din vet hva som slippes inn.',
  'Phones and tablets that register counts, and the codes that let them in.':
    'Telefoner og nettbrett som registrerer tellinger, og kodene som slipper dem inn.',
  'No company': 'Ingen selskap',
  'This account is not a member of a company.':
    'Denne kontoen er ikke medlem av noe selskap.',
  /* The open door. Norwegian first, because the person deciding whether to
     open it is standing on a walkway handing out phones. */
  'This device': 'Denne enheten',
  'The role decides which farms this device sees — and therefore how much of the company is on a screen anyone can pick up. What it may change is still only counts and the logbook.':
    'Rollen bestemmer hvilke lokaliteter denne enheten ser — og dermed hvor mye av selskapet som ligger på en skjerm hvem som helst kan plukke opp. Den kan fortsatt bare endre tellinger og loggbok.',
  'Open connection': 'Åpen tilkobling',
  'While this is open, anyone who types one of your codes is let straight in and nobody is asked. Use it while you are handing out phones — it closes on its own.':
    'Mens dette er åpent, slipper alle som taster en av kodene deres rett inn, uten at noen blir spurt. Bruk det mens dere deler ut telefoner — det lukker seg selv.',
  'Open': 'Åpen',
  'closes in': 'lukker om',
  'opened by': 'åpnet av',
  'Close it now': 'Lukk nå',
  'Closing…': 'Lukker…',
  'hour': 'time',
  'hours': 'timer',
  'minutes': 'minutter',
  'Already waiting, and now let in': 'Ventet allerede, og er nå sluppet inn',
  'Left waiting, because the code they used has since been withdrawn':
    'Står fortsatt og venter, fordi koden de brukte er trukket tilbake',
  'Codes': 'Koder',
  'New code': 'Ny kode',
  'No codes yet. Make one and hand it to whoever does the counting.':
    'Ingen koder ennå. Lag en og gi den til den som teller.',
  'Unnamed code': 'Kode uten navn',
  'the whole company': 'hele selskapet',
  'used': 'brukt',
  'devices': 'enheter',
  'revoked': 'trukket tilbake',
  'expired': 'utløpt',
  'used up': 'oppbrukt',
  'Rotate': 'Bytt kode',
  'Replace the characters. Devices already connected are untouched.':
    'Bytt ut tegnene. Enheter som allerede er tilkoblet berøres ikke.',
  'Revoke it': 'Trekk den tilbake',
  'No device is using this code.': 'Ingen enhet bruker denne koden.',
  'devices already connected keep working.':
    'enheter som allerede er tilkoblet fortsetter å virke.',

  'Write this down now.': 'Skriv den ned nå.',
  'It is not stored and cannot be shown again. If it is lost, rotate the code — nobody already connected is affected.':
    'Den lagres ikke og kan ikke vises igjen. Mister du den, bytter du koden — ingen som allerede er tilkoblet berøres.',
  'Copy': 'Kopier',
  'Copied': 'Kopiert',
  'Print': 'Skriv ut',
  'Reusable. Anyone with it can connect a device.':
    'Kan brukes flere ganger. Alle som har den kan koble til en enhet.',
  'One device only.': 'Bare én enhet.',
  'Good until': 'Gyldig til',
  'Or scan this': 'Eller skann denne',
  'Done': 'Ferdig',

  'What to call it': 'Hva den skal hete',
  'Merd 3, or Lokalitet Nord': 'Merd 3, eller Lokalitet Nord',
  'Also what a device is called until somebody types a name on it.':
    'Også det en enhet heter inntil noen skriver inn et navn på den.',
  'Where it may count': 'Hvor den kan telle',
  'The whole company': 'Hele selskapet',
  'Area': 'Område',
  'As': 'Som',
  'Shape': 'Form',
  'A card for the crew': 'Et kort til mannskapet',
  'Reusable, good for a year. Typed in, never in a link.':
    'Kan brukes flere ganger, gyldig i et år. Skrives inn, aldri i en lenke.',
  'A QR for one device': 'En QR til én enhet',
  'One use, two weeks. For a phone standing in front of you.':
    'Én gangs bruk, to uker. Til en telefon som står foran deg.',
  'Making it…': 'Lager den…',
  'Make the code': 'Lag koden',
  'Nobody would be told when a device connects. Add a company manager first.':
    'Ingen ville fått beskjed når en enhet kobler seg til. Legg til en bedriftsansvarlig først.',

  'Connected devices': 'Tilkoblede enheter',
  'Nothing connected yet.': 'Ingenting tilkoblet ennå.',
  'nobody has named it': 'ingen har gitt den navn',
  'Rename': 'Gi nytt navn',
  'Disconnect': 'Meld ut',
  'Disconnect it': 'Meld den ut',
  'It stops at its next request. Everything it counted stays, still under its name.':
    'Den stopper ved neste forespørsel. Alt den har telt blir stående, fortsatt under navnet sitt.',
  'never used': 'aldri brukt',
  'seen today': 'sett i dag',
  'seen yesterday': 'sett i går',
  'last seen': 'sist sett for',
  'days ago': 'dager siden',

  /* Strøm. Et varsel og ikke en måling, og ordene bærer den forskjellen:
     «modellert» står i teksten hver gang et tall står ved siden av. */
  'Current': 'Strøm',
  'slack water': 'stille vann',
  'no forecast this hour': 'ikke varsel for denne timen',
  'Hour of the current forecast': 'Time i strømvarselet',
  /* Flo og fjære, som de heter på kaia. Ikke «høyvann»/«lavvann», som er
     riktig og som ingen sier. */
  'tide:high': 'flo',
  'tide:low': 'fjære',
  /* Utskifting: hvor mye nytt vann merden får. IKKE en «mer er bedre»-skala —
     over omtrent 0,35 m/s deformeres nota og fisken står og svømmer. */
  'exchange:slack': 'stille vann',
  'exchange:weak': 'svak utskifting',
  'exchange:moderate': 'moderat utskifting',
  'exchange:good': 'god utskifting',
  'exchange:strong': 'sterk strøm',
  'Modelled, not measured.': 'Modellert, ikke målt.',
  'One vector for the whole farm — the model’s grid is about a kilometre across, so what differs between the cages here is geometry.':
    'Én vektor for hele anlegget — modellens rutenett er omtrent en kilometer, '
    + 'så det som skiller merdene her er geometri.',
  'The forecast is unreachable — this is the last one we fetched.':
    'Varselet er utilgjengelig — dette er det siste vi hentet.',
  'No arrows for {n}: nobody has placed them on the map yet.':
    'Ingen piler for {n}: ingen har plassert dem på kartet ennå.',
  'What this assumes': 'Hva dette forutsetter',

  /* Oksygen: en rangering, aldri en avlesning. Ordene må bære det, for et tall
     med desimaler leses som en måling uansett hva som står under det. */
  'Flushing, best to worst': 'Utskifting, best til dårligst',
  /* 'Pen' er allerede i ordboka lenger oppe og gjenbrukes her. */
  'behind {n}': 'bak {n}',
  /* Hvor fôringstallet kom fra. «Usikker» er den viktige: en båt ved anlegget
     er høyere enn en blåser i det samme båndet, og en stille time under den er
     ikke bevis for at fôringen stoppet. Samme skille som fôringsindeksen
     gjør for et helt døgn med rigg på anlegget. */
  'feeding:measured': 'Fôring: hørt på anlegget denne timen',
  'feeding:uncertain': 'Fôring: usikker denne timen',
  'feeding:expected': 'Fôring: antatt etter anleggets vanlige tider',
  /* Hvorfor merdene ikke kunne rangeres. Koder fra tjenesten, ord her. */
  'oxygen:no_biomass':
    'Ingen rangering: ingen har ført inn hvor mange fisk som står i merdene, '
    + 'eller hva de veier.',
  'oxygen:water_too_slow':
    'Ingen rangering: vannet står for stille til at utskiftingen drives av '
    + 'strømmen denne timen.',
  'oxygen:no_current_or_temperature':
    'Ingen rangering: varselet mangler strøm eller sjøtemperatur denne timen.',
  'oxygen:nothing_scorable': 'Ingen rangering for denne timen.',
  'feeding:why:masked_by_vessel':
    'en båt lå ved anlegget, og den er høyere enn blåseren i det samme båndet',
  'feeding:why:unresolved_band': 'båndet hadde ikke rom til å måle blåseren',
  'A comparison between these cages, not a measurement of any of them. No oxygen concentration is computed.':
    'En sammenlikning mellom disse merdene, ikke en måling av noen av dem. '
    + 'Ingen oksygenverdi er regnet ut.',
}


const DICTS: Record<Lang, Record<string, string>> = { nb: NB, en: {} }

const KEY = 'phoniq.lang'

/** The language to start in.
 *
 *  A stored choice wins, then the browser's, then Norwegian. Norwegian is the
 *  fallback rather than English because a Norwegian operator who has never
 *  touched a setting should not have to.
 */
export function initialLang(): Lang {
  try {
    const saved = localStorage.getItem(KEY)
    if (saved === 'nb' || saved === 'en') return saved
  } catch {
    /* private mode, or storage disabled: fall through to the browser */
  }
  const nav = typeof navigator !== 'undefined' ? navigator.language : ''
  return nav.toLowerCase().startsWith('en') ? 'en' : 'nb'
}

export function storeLang(lang: Lang): void {
  try {
    localStorage.setItem(KEY, lang)
  } catch {
    /* not being able to remember the choice is not a reason to refuse it */
  }
}

/** Translate, falling back to the key.
 *
 *  The key is English prose, so an untranslated string degrades to correct
 *  English rather than to `nav.listening.title`. That matters on a half-migrated
 *  screen, which is every screen during a migration.
 */
export function translate(lang: Lang, key: string): string {
  return DICTS[lang][key] ?? key
}
